import { SessionState } from './Enums';
import { Reasons } from './Reasons/Reasons';
import Reason from './Reasons/Reason';
import MessageService from './MessageService';
import RedirectService from './RedirectService';
import CookieService from './Cookies/CookieService';
import SessionService from './SessionService';

export default class ErrorService {
    /**
     * ErrorService constructor
     * @param {MessageService} messageService
     * @param {RedirectService} redirectService
     * @param {CookieService} cookieService
     * @param {SessionService} sessionService
     * @param {object} options
     */
    constructor(messageService, redirectService, cookieService, sessionService, options) {
        this.messageService = messageService;
        this.redirectService = redirectService;
        this.cookieService = cookieService;
        this.sessionService = sessionService;
        this.options = options;
    }

    async error(reasonId, message, showTryAgainButton = false, originalReason = undefined, updateSessionState = true) {        
        const reason = originalReason != undefined ? originalReason : new Reason(reasonId, message || 'message for reason');
        const errorMessage = reason._messageKey;

        if (updateSessionState) {
            await this._updateSessionState(SessionState.fail, reason, errorMessage);
        }

        this.displayErrorMessage(reason.getUIMessage(), false, reason._hintKey, false, true, showTryAgainButton);
    }

    cookieConsentDenied() {
        const reason = Reasons.cookie_consent_denied;
        this.redirectService.handleScreenOut(reason.getUIMessage(), true);
    }

    async aborted(errorMessage) {
        const reason = Reasons.collection_aborted_by_user;
        errorMessage = errorMessage ?? reason._messageKey;

        await this._updateSessionState(SessionState.declined, reason, errorMessage);

        this.redirectService.handleScreenOut(reason.getUIMessage(), true);
    }

    async mediaServerConnectionLost() {
        const reason = Reasons.mediaserver_connection_lost;

        await this._updateSessionState(SessionState.fail, reason, reason._messageKey);

        this.displayErrorMessage('mediaserver_connection_lost');
    }

    async webCamDenied(errorMessage) {
        const reason = Reasons.webcam_access_denied;
        errorMessage = errorMessage ?? reason._messageKey;

        let denyCount = this.cookieService.getCookie('WebcamDenyCount');
        denyCount = denyCount ? Number(denyCount) + 1 : 1;
        this.cookieService.setCookie('WebcamDenyCount', denyCount, 1);

        await this._updateSessionState(SessionState.declined, reason, errorMessage);

        if (denyCount <= this.options.webcamDenyRetries) {
            this.displayErrorMessage(reason.getUIMessage());
        } else {
            this.redirectService.handleScreenOut(reason.getUIMessage(), true);
            this.cookieService.setCookie('WebcamDenyCount', 0, 1);
        }
    }

    async noWebCamActivities() {
        const reason = Reasons.no_webcam_activites;

        await this._updateSessionState(SessionState.fail, reason, reason._messageKey);

        this.displayErrorMessage('cam_busy');
    }

    async webcamAccessTimeout(timeout) {
        const reason = Reasons.webcam_access_timeout;

        await this._updateSessionState(SessionState.fail, reason, reason._messageKey);

        const message = this.messageService.uiMessages.webcam_access_timeout.replace('{TIMEOUT}', timeout);
        this.displayErrorMessage(message);
    }

    async faceDuplication() {
        const reason = Reasons.face_duplication;
        await this._updateSessionState(SessionState.fail, reason, 'face_duplication', false);
    }

    async _updateSessionState(stateId, reason, errorMessage, broadcastSessionState = true) {
        await this.sessionService.updateSession(
            {
                stateId: stateId,
                reason: reason,
                reasonMessage: errorMessage,
                logs: [
                    {
                        logType: 'error',
                        logMessage: errorMessage
                    }
                ]
            },
            broadcastSessionState
        );
    }
    async displayFaceDuplicationMessage() {
        const reason = Reasons.face_duplication;
        await this.faceDuplication();
        this.messageService.displayMessage(reason.getUIMessage());
    }

    displayErrorMessage(
        message,
        clearMessages,
        hintMessageKey,
        handleWindowClose = true,
        doScreenOut = false,
        showTryAgainButton = true
    ) {
        let buttons;
        if (showTryAgainButton) {
            buttons = {
                tryAgain: {
                    message: this.messageService.uiMessages.try_again,
                    callback: (e) => {
                        e.preventDefault();

                        if (handleWindowClose) {
                            this.redirectService.needHandleWindowClose(false);
                        }
                        window.location.reload(false);
                    }
                }
            };
        }

        if (doScreenOut) {
            this.redirectService.handleScreenOut(message, false, buttons);
        } else {
            this.messageService.displayMessage(message, undefined, buttons, clearMessages, hintMessageKey);
        }
    }
}
